<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import UserTeamMultiselect from 'dashboard/components/UserTeamMultiselect.vue';

const props = defineProps({
  categoryKey: { type: String, required: true },
  categoryLabel: { type: String, required: true },
});

const emit = defineEmits(['close']);

const closeModal = () => emit('close');

const store = useStore();
const { t } = useI18n();

const selectedUserIds = ref([]);
const selectedTeamIds = ref([]);
const saving = ref(false);

const uiFlags = computed(
  () => store.getters['customAttributeCategoryAssignments/getUIFlags']
);

const loadSelections = () => {
  const userIds = store.getters[
    'customAttributeCategoryAssignments/getUserIdsForCategory'
  ](props.categoryKey);
  const teamIds = store.getters[
    'customAttributeCategoryAssignments/getTeamIdsForCategory'
  ](props.categoryKey);
  selectedUserIds.value = [...userIds];
  selectedTeamIds.value = [...teamIds];
};

onMounted(async () => {
  if (!store.getters['agents/getAgents']?.length) {
    await store.dispatch('agents/get');
  }
  if (!store.getters['teams/getTeams']?.length) {
    await store.dispatch('teams/get');
  }
  if (!store.getters['customAttributeCategoryAssignments/getRecords'].length) {
    await store.dispatch('customAttributeCategoryAssignments/get');
  }
  loadSelections();
});

watch(
  () => props.categoryKey,
  () => loadSelections()
);

const saveAssignments = async () => {
  saving.value = true;
  try {
    await store.dispatch('customAttributeCategoryAssignments/update', {
      categoryKey: props.categoryKey,
      userIds: selectedUserIds.value,
      teamIds: selectedTeamIds.value,
    });
    useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.SUCCESS'));
    emit('close');
  } catch {
    useAlert(t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div
      class="w-full max-w-lg rounded-xl bg-white dark:bg-slate-800 shadow-2xl p-6 mx-4"
    >
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
        {{ categoryLabel }}
      </p>

      <div class="mt-5">
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.TITLE') }}
        </label>
        <UserTeamMultiselect
          v-model:user-ids="selectedUserIds"
          v-model:team-ids="selectedTeamIds"
          :placeholder="t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.PLACEHOLDER')"
        />
      </div>

      <div class="mt-5 flex items-center gap-2">
        <Button
          :label="t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.CANCEL')"
          slate
          outlined
          class="w-full"
          @click="closeModal"
        />
        <Button
          :label="t('ATTRIBUTES_MGMT.CATEGORIES.ASSIGNMENTS.SAVE')"
          class="w-full"
          :is-loading="saving || uiFlags.isUpdating"
          @click="saveAssignments"
        />
      </div>
    </div>
  </div>
</template>
